import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Calendar, RefreshCw, Link2, Unlink, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/services/api';
interface GoogleCalendar {
  id: string;
  name: string;
  description?: string;
  primary: boolean;
  accessRole: string;
  backgroundColor?: string;
}

interface CalendarConnection {
  id: string;
  provider: 'google' | 'outlook';
  name: string;
  email: string;
  connected: boolean;
  calendarId?: string;
  calendarName?: string;
  lastSync?: string;
  status?: string;
  syncDirection: 'pull' | 'push' | 'bidirectional';
  isPrimary: boolean;
  conflictCheckEnabled: boolean;
  displayColor?: string;
}

interface CalendarProvider {
  id: string;
  name: string;
  available: boolean;
  icon: string;
}

interface CalendarSettingsProps {
  clinicId?: string;
}

const CalendarSettings: React.FC<CalendarSettingsProps> = ({ clinicId }) => {
  const [connections, setConnections] = useState<CalendarConnection[]>([]);
  const [providers, setProviders] = useState<CalendarProvider[]>([]);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<string>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const { toast } = useToast();

  // Check URL for success/error params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (success) {
      toast({
        title: 'Calendar Connected',
        description: `Successfully connected your ${success === 'google' ? 'Google' : 'Outlook'} Calendar`,
      });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (error) {
      const errorMessages: Record<string, string> = {
        oauth_denied: 'Calendar connection was denied',
        missing_params: 'Invalid callback parameters',
        invalid_state: 'Invalid authentication state',
        connection_failed: 'Failed to connect calendar',
      };
      toast({
        title: 'Connection Failed',
        description: errorMessages[error] || 'Failed to connect calendar',
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Fetch calendar connections and providers
  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/calendar/connections');
      
      if (response.data.success) {
        setConnections(response.data.connections || []);
        setProviders(response.data.providers || []);
        setSyncEnabled(response.data.syncEnabled);
        setLastSync(response.data.lastSync);
        setSyncStatus(response.data.syncStatus);
        setSyncError(response.data.syncError);
      }
    } catch (error) {
      console.error('Failed to fetch calendar connections:', error);
      toast({
        title: 'Error',
        description: 'Failed to load calendar settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const response = await apiClient.get('/calendar/google/calendars');
      if (response.data.success) {
        setGoogleCalendars(response.data.calendars || []);
        setSelectedCalendarIds(
          response.data.selectedCalendarIds || [response.data.selectedCalendarId || 'primary']
        );
      }
    } catch (error) {
      console.error('Failed to fetch Google calendars:', error);
    } finally {
      setLoadingCalendars(false);
    }
  };

  // Fetch sub-calendars when Google is connected
  useEffect(() => {
    if (connections.some(c => c.provider === 'google')) {
      fetchGoogleCalendars();
    } else {
      setGoogleCalendars([]);
      setSelectedCalendarIds([]);
    }
  }, [connections]);

  const handleCalendarToggle = async (calendarId: string, checked: boolean) => {
    const newIds = checked
      ? [...selectedCalendarIds, calendarId]
      : selectedCalendarIds.filter(id => id !== calendarId);

    if (newIds.length === 0) {
      toast({
        title: 'Error',
        description: 'At least one calendar must be selected',
        variant: 'destructive',
      });
      return;
    }

    setSelectedCalendarIds(newIds);
    try {
      await apiClient.put('/calendar/google/calendar', { calendarIds: newIds });
      toast({
        title: 'Calendars Updated',
        description: 'Your calendar selection has been saved',
      });
    } catch (error: any) {
      // Revert on error
      setSelectedCalendarIds(selectedCalendarIds);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update calendar selection',
        variant: 'destructive',
      });
    }
  };

  const handleConnect = async (providerId: string) => {
    try {
      setConnecting(providerId);
      const endpoint = providerId === 'google' ? '/calendar/google/auth' : '/calendar/outlook/auth';
      const response = await apiClient.get(endpoint, {
        params: { clinicId }
      });

      if (response.data.success && response.data.authUrl) {
        // Redirect to OAuth provider
        window.location.href = response.data.authUrl;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error: any) {
      console.error('Failed to initiate connection:', error);
      toast({
        title: 'Connection Failed',
        description: error.response?.data?.message || 'Failed to connect calendar',
        variant: 'destructive',
      });
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider: string) => {
    try {
      setConnecting(provider);
      await apiClient.delete(`/calendar/connections/${provider}`);
      
      toast({
        title: 'Disconnected',
        description: `${provider === 'google' ? 'Google' : 'Outlook'} Calendar disconnected`,
      });
      
      fetchConnections();
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to disconnect calendar',
        variant: 'destructive',
      });
    } finally {
      setConnecting(null);
    }
  };

  const handleSyncToggle = async (enabled: boolean) => {
    try {
      await apiClient.put('/calendar/settings', { syncEnabled: enabled });
      setSyncEnabled(enabled);
      
      toast({
        title: enabled ? 'Sync Enabled' : 'Sync Disabled',
        description: enabled 
          ? 'Your calendar will sync automatically every 15 minutes' 
          : 'Calendar sync has been disabled',
      });

      if (enabled) {
        // Trigger immediate sync
        handleSync();
      }
    } catch (error: any) {
      console.error('Failed to update settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update calendar settings',
        variant: 'destructive',
      });
    }
  };

  const handleSetPrimary = async (provider: string) => {
    try {
      await apiClient.put(`/calendar/connections/${provider}/primary`);
      
      // Update local state
      setConnections(prev => prev.map(conn => ({
        ...conn,
        isPrimary: conn.provider === provider,
      })));
      
      toast({
        title: 'Primary Calendar Set',
        description: 'This calendar will now be used for new appointments',
      });
    } catch (error: any) {
      console.error('Failed to set primary calendar:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to set primary calendar',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateConnection = async (provider: string, updates: Partial<CalendarConnection>) => {
    try {
      await apiClient.put(`/calendar/connections/${provider}`, updates);
      
      // Update local state
      setConnections(prev => prev.map(conn => 
        conn.provider === provider ? { ...conn, ...updates } : conn
      ));
      
      toast({
        title: 'Settings Updated',
        description: 'Calendar settings have been updated',
      });
    } catch (error: any) {
      console.error('Failed to update connection:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update settings',
        variant: 'destructive',
      });
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncStatus('syncing');
      
      const response = await apiClient.post('/calendar/sync');
      
      if (response.data.success) {
        setLastSync(new Date().toISOString());
        setSyncStatus('idle');
        setSyncError(null);
        
        toast({
          title: 'Sync Complete',
          description: `Synced ${response.data.result?.totalEvents || 0} external events`,
        });
      }
    } catch (error: any) {
      console.error('Sync failed:', error);
      setSyncStatus('error');
      setSyncError(error.response?.data?.error || 'Sync failed');
      
      toast({
        title: 'Sync Failed',
        description: error.response?.data?.message || 'Failed to sync calendar',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const formatLastSync = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  const getProviderIcon = (provider: string) => {
    if (provider === 'google') {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#0078D4" d="M11.5 2.5v9h9v-9h-9zm-9 0v9h9v-9h-9zm0 9v9h9v-9h-9zm9 0v9h9v-9h-9z"/>
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasAnyConnection = connections.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Calendar Integration
          </CardTitle>
          <CardDescription>
            Connect your Google or Outlook calendar to automatically block busy times in your NovaMed schedule.
            External calendar events will prevent patients from booking during those times.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sync Toggle */}
          {hasAnyConnection && (
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="sync-toggle" className="font-medium">Automatic Sync</Label>
                <p className="text-sm text-muted-foreground">
                  Sync calendar events every 15 minutes
                </p>
              </div>
              <Switch
                id="sync-toggle"
                checked={syncEnabled}
                onCheckedChange={handleSyncToggle}
              />
            </div>
          )}

          {/* Sync Status */}
          {hasAnyConnection && (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {syncStatus === 'syncing' ? (
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                ) : syncStatus === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-destructive" />
                ) : (
                  <Clock className="w-5 h-5 text-muted-foreground" />
                )}
                <div>
                  <p className="text-sm font-medium">
                    {syncStatus === 'syncing' ? 'Syncing...' : `Last synced: ${formatLastSync(lastSync)}`}
                  </p>
                  {syncError && (
                    <p className="text-xs text-destructive">{syncError}</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Sync Now
              </Button>
            </div>
          )}

          {/* Connected Calendars */}
          {connections.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium">Connected Calendars</h4>
              {connections.map((conn) => (
                <div
                  key={conn.id || conn.provider}
                  className="border rounded-lg bg-green-50 border-green-200"
                >
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      {getProviderIcon(conn.provider)}
                      <div>
                        <p className="font-medium">{conn.calendarName || conn.name}</p>
                        <p className="text-sm text-muted-foreground">
                          <CheckCircle className="w-3 h-3 inline mr-1 text-green-600" />
                          {conn.email}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(conn.provider)}
                      disabled={connecting === conn.provider}
                    >
                      {connecting === conn.provider ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Unlink className="w-4 h-4 mr-2" />
                      )}
                      Disconnect
                    </Button>
                  </div>

                  {/* Sub-calendar selection for Google */}
                  {conn.provider === 'google' && googleCalendars.length > 0 && (
                    <div className="px-4 pb-4 border-t border-green-200">
                      <p className="text-sm font-medium text-muted-foreground mt-3 mb-2">
                        Check for conflicts on
                      </p>
                      {loadingCalendars ? (
                        <div className="flex items-center gap-2 py-2">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Loading calendars...</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {googleCalendars.map((cal) => (
                            <label
                              key={cal.id}
                              className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-green-100/50 cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedCalendarIds.includes(cal.id)}
                                onCheckedChange={(checked) =>
                                  handleCalendarToggle(cal.id, checked === true)
                                }
                              />
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: cal.backgroundColor || '#4285F4' }}
                              />
                              <span className="text-sm">
                                {cal.name}
                                {cal.primary && (
                                  <span className="text-xs text-muted-foreground ml-1">(Primary)</span>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Available Providers */}
          <div className="space-y-3">
            <h4 className="font-medium">
              {hasAnyConnection ? 'Add Another Calendar' : 'Connect a Calendar'}
            </h4>
            {providers
              .filter(p => p.available && !connections.find(c => c.provider === p.id))
              .map((provider) => (
                <div
                  key={provider.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getProviderIcon(provider.id)}
                    <div>
                      <p className="font-medium">{provider.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Sync your busy times from {provider.name}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleConnect(provider.id)}
                    disabled={connecting === provider.id}
                  >
                    {connecting === provider.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Connect
                  </Button>
                </div>
              ))}

            {providers.filter(p => !p.available).length > 0 && (
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Some calendar providers are not configured. Contact your administrator to enable them.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Info */}
          <Alert>
            <Calendar className="w-4 h-4" />
            <AlertDescription>
              <strong>How it works:</strong> When you connect a calendar, NovaMed will sync your busy times 
              every 15 minutes. Patients won't be able to book appointments during times when you have 
              events marked as "Busy" in your external calendar.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarSettings;


